import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { HijriMonthGrid } from "@/components/calendar/HijriMonthGrid";
import { HijriMiniMonthLegend } from "@/components/calendar/HijriMiniMonthLegend";
import { PdfExportButton } from "@/components/PdfExportButton";
import { HIJRI_EVENTS } from "@/lib/calendar/hijri-events";
import { HIJRI_MONTHS, HIJRI_MONTHS_EN, hijriToGregorian, currentHijriYear, daysInHijriMonth } from "@/lib/calendar/hijri";
import { calendarDatePath } from "@/lib/calendar/dates";
import { useLocale } from "@/i18n/LocaleContext";
import type { Locale } from "@/i18n/types";
import { LongWeekendTable } from "@/components/pages/PseoExtras";
import { getHolidaysForYear } from "@/data/holidays";
import { SourcesNote } from "@/components/SourcesNote";
import { hijriMonthEntities, calendarEntities } from "@/lib/seo/entities";
import { datasetSchema } from "@/lib/seo/schema";

export function HijriYearPage({ hYear, locale }: { hYear: number; locale?: Locale }) {
  return (
    <SiteShell locale={locale}>
      <Body hYear={hYear} />
    </SiteShell>
  );
}

function Body({ hYear }: { hYear: number }) {
  const { t, href, locale } = useLocale();
  const startG = hijriToGregorian(hYear, 1, 1);
  const lastG = hijriToGregorian(hYear, 12, daysInHijriMonth(hYear, 12));
  const cur = currentHijriYear();
  const overlapYears = Array.from(new Set([startG.y, lastG.y]));
  const datasetLd = datasetSchema({
    name: `Kalender Hijriah ${hYear} H`,
    description: `Kalender Hijriah tahun ${hYear} H dengan padanan Masehi, peristiwa Islam (Muharram–Zulhijah), Maulid, Isra Mikraj, Ramadhan, Idul Fitri, dan Idul Adha.`,
    url: `/kalender-hijriah/${hYear}/`,
    locale: locale === "en" ? "en" : "id",
    temporalCoverage: `${startG.y}-${String(startG.m).padStart(2, "0")}-${String(startG.d).padStart(2, "0")}/${lastG.y}-${String(lastG.m).padStart(2, "0")}-${String(lastG.d).padStart(2, "0")}`,
    about: [...hijriMonthEntities(), ...calendarEntities()],
  });

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(datasetLd) }} />
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">{t.common.home}</Link>
        {" / "}
        <Link to={href("/kalender-hijriah") as never} className="hover:text-primary">{t.common.hijriCalendar}</Link>
        {" / "}{hYear} H
      </nav>

      <header className="mb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t.common.hijriCalendarFull} {hYear} {locale === "en" ? "AH" : "H"}
            </h1>
            <p className="mt-1 text-muted-foreground">
              {locale === "en"
                ? `Starts on ${startG.d} ${t.common.months[startG.m - 1]} ${startG.y}.`
                : `Dimulai pada ${startG.d} ${t.common.months[startG.m - 1]} ${startG.y}.`}
            </p>
          </div>
          <PdfExportButton mode="h-year" year={hYear} hYear={hYear} allowSwitch />
        </div>
      </header>

      {/* Quick year jump */}
      <div className="mb-3 flex flex-wrap gap-2 text-sm">
        {[cur - 1, cur, cur + 1, hYear].filter((v, i, a) => a.indexOf(v) === i).map((y) => (
          <Link
            key={y}
            to={href(`/kalender-hijriah/${y}/`) as never}
            className={
              "rounded-md border border-border px-3 py-1 hover:bg-accent " +
              (y === hYear ? "bg-accent font-semibold" : "")
            }
          >
            {y} H
          </Link>
        ))}
      </div>

      {/* Year selector ±50 */}
      <details className="mb-6 rounded-lg border border-border bg-card">
        <summary className="cursor-pointer px-4 py-2 text-sm font-medium">
          {locale === "en" ? "Choose another Hijri year (±50)" : "Pilih tahun Hijriah lain (±50 tahun)"}
        </summary>
        <div className="border-t border-border p-3">
          <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-10">
            {Array.from({ length: 101 }, (_, i) => cur - 50 + i).map((y) => (
              <Link
                key={y}
                to={href(`/kalender-hijriah/${y}/`) as never}
                className={
                  "rounded border border-border px-2 py-1 text-center text-xs hover:bg-accent " +
                  (y === hYear ? "bg-primary/10 font-semibold text-primary" : "")
                }
              >
                {y}
              </Link>
            ))}
          </div>
        </div>
      </details>

      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2">
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
          <div key={m} className="space-y-1">
            <HijriMonthGrid
              hYear={hYear}
              hMonth={m}
              align="auto"
              monthHref={href(`/kalender-hijriah/${hYear}/${m}/`)}
            />
            <HijriMiniMonthLegend hYear={hYear} hMonth={m} />
          </div>
        ))}
      </div>

      <section className="mt-8 rounded-lg border border-border bg-card p-5">
        <h2 className="text-lg font-semibold">
          {locale === "en" ? `Months in ${hYear} AH` : `Bulan dalam ${hYear} H`}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {locale === "en"
            ? "Open a dedicated page for each Hijri month with Gregorian dates, Islamic events, and Indonesian holidays."
            : "Buka halaman khusus tiap bulan Hijriah lengkap dengan tanggal Masehi, hari penting Islam, dan libur nasional."}
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {(locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS).map((name, i) => {
            const m = i + 1;
            const g = hijriToGregorian(hYear, m, 1);
            return (
              <Link
                key={name}
                to={href(`/kalender-hijriah/${hYear}/${m}/`) as never}
                className="block rounded-md border border-border bg-card px-3 py-2 text-sm hover:bg-accent"
              >
                <div className="font-medium">{name} {hYear} {locale === "en" ? "AH" : "H"}</div>
                <div className="text-xs text-muted-foreground">
                  {locale === "en" ? "from " : "mulai "}{g.d} {t.common.months[g.m - 1]} {g.y}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="mt-8 rounded-lg border border-border bg-card p-4">
        <h2 className="mb-3 text-lg font-semibold">{t.common.importantIslamicDays} {hYear} H</h2>
        <ul className="grid gap-1.5 text-sm sm:grid-cols-2">
          {HIJRI_EVENTS.map((ev) => {
            const g = hijriToGregorian(hYear, ev.hMonth, ev.hDay);
            const monthName = (locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS)[ev.hMonth - 1];
            const name = locale === "en" ? ev.nameEn : ev.name;
            const dotClass = ev.type === "hari-besar" ? "bg-emerald-500" : ev.type === "puasa" ? "bg-amber-500" : "bg-muted-foreground";
            return (
              <li key={`${ev.hMonth}-${ev.hDay}`} className="flex items-start gap-2">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
                <Link to={href(calendarDatePath(g.y, g.m, g.d)) as never} className="hover:text-primary">
                  <span className="font-medium">{ev.hDay} {monthName}</span>
                  <span className="text-muted-foreground"> - {name}</span>
                  <div className="text-xs text-muted-foreground">{g.d} {t.common.months[g.m - 1]} {g.y}</div>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      <p className="mt-6 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        {locale === "en"
          ? "Hijri dates use the arithmetic civil (Kuwaiti) calendar and may differ by one day from moon-sighting. Official Indonesian dates for Ramadan, Eid, and Dhul-Hijjah follow the Kemenag isbat session."
          : "Tanggal Hijriah memakai algoritma aritmetika sipil (Kuwaiti) dan dapat berbeda 1 hari dari rukyatul hilal. Penetapan resmi 1 Ramadhan, Idul Fitri, dan Idul Adha mengikuti sidang isbat Kemenag."}
      </p>

      <section className="mt-8 rounded-lg border border-border bg-card p-5">
        <h2 className="text-lg font-semibold">{locale === "en" ? "Data Sources" : "Sumber Data"}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {locale === "en"
            ? "Hijri dates are computed from the arithmetic civil (Kuwaiti) calendar epoch JD 1948440. Indonesian official decisions for Ramadan/Eid follow Kemenag's isbat session."
            : "Tanggal Hijriah dihitung memakai kalender aritmetika sipil (Kuwaiti) dengan epoch JD 1948440. Penetapan resmi awal Ramadhan, Idul Fitri, dan Idul Adha di Indonesia mengikuti sidang isbat Kementerian Agama RI."}
        </p>
        <div className="mt-3">
          <SourcesNote variant="kalender" />
        </div>
      </section>

      <div className="mt-8 space-y-6">
        {overlapYears.map((gy) => (
          <LongWeekendTable
            key={gy}
            year={gy}
            holidays={getHolidaysForYear(gy)}
            heading={
              locale === "en"
                ? `Long weekend & harpitnas ${gy}`
                : `Long weekend & harpitnas ${gy}`
            }
          />
        ))}
      </div>
    </>
  );
}